import React, { useState, useRef, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  Terminal, Send, X, Loader2, CheckCircle2, AlertTriangle,
  Play, Rocket, Settings, Plus, ChevronDown, MessageCircle
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MODES = [
  { value: 'chat', label: 'Chat' },
  { value: 'create', label: 'Create' },
  { value: 'explain', label: 'Explain' },
  { value: 'propose_patch', label: 'Edit' },
  { value: 'simulate', label: 'Test' },
  { value: 'deploy', label: 'Deploy' },
];

const CommandBar = ({ blueprintId, blueprintVersion, agentId, businessId }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [input, setInput] = useState('');
  const [mode, setMode] = useState('chat');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [pendingPatch, setPendingPatch] = useState(null);
  const [selectedBpId, setSelectedBpId] = useState(blueprintId || null);
  const [selectedBizId, setSelectedBizId] = useState(businessId || null);
  const inputRef = useRef(null);

  const page = location.pathname;
  const hasContext = selectedBpId || agentId;

  useEffect(() => { if (open) inputRef.current?.focus(); }, [open]);
  useEffect(() => { setSelectedBpId(blueprintId); }, [blueprintId]);
  useEffect(() => { setSelectedBizId(businessId); }, [businessId]);

  useEffect(() => {
    const handler = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') { e.preventDefault(); setOpen(prev => !prev); }
      if (e.key === 'Escape') setOpen(false);
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, []);

  const handleSend = async () => {
    if (!input.trim() && !['simulate', 'deploy'].includes(mode)) return;
    setLoading(true);
    setResult(null);
    setPendingPatch(null);

    try {
      const res = await axios.post(`${API}/operator/command`, {
        mode,
        message: input.trim(),
        context: {
          page,
          blueprint_id: selectedBpId,
          blueprint_version: blueprintVersion,
          agent_id: agentId,
          business_id: selectedBizId,
          proposed_patch: pendingPatch,
        },
      });
      const data = res.data;
      setResult(data);
      setInput('');

      if (data.proposed_patch) setPendingPatch(data.proposed_patch);
      if (data.blueprint_id) setSelectedBpId(data.blueprint_id);
      if (data.open_integrations) toast.info('Use Integrations settings for credentials');
    } catch (err) {
      setResult({ assistant_message: err.response?.data?.detail || 'Command failed' });
    } finally { setLoading(false); }
  };

  const handleApply = async () => {
    if (!pendingPatch || !selectedBpId) return;
    setLoading(true);
    try {
      const res = await axios.post(`${API}/operator/command`, {
        mode: 'apply_patch', message: '',
        context: { blueprint_id: selectedBpId, blueprint_version: blueprintVersion, proposed_patch: pendingPatch },
      });
      setResult(res.data);
      setPendingPatch(null);
      toast.success('Patch applied');
    } catch { toast.error('Apply failed'); }
    finally { setLoading(false); }
  };

  // Auto-detect mode from input
  const handleInputChange = (val) => {
    setInput(val);
    const lower = val.toLowerCase();
    if (lower.startsWith('create ') || lower.startsWith('build ') || lower.startsWith('make ')) setMode('create');
    else if (lower.startsWith('explain ') || lower.startsWith('what ') || lower.endsWith('?')) setMode('chat');
    else if (lower.startsWith('change ') || lower.startsWith('edit ') || lower.startsWith('add ') || lower.startsWith('remove ')) setMode('propose_patch');
    else if (lower.startsWith('test') || lower.startsWith('simulate')) setMode('simulate');
    else if (lower.startsWith('deploy') || lower.startsWith('launch')) setMode('deploy');
  };

  if (!open) {
    return (
      <div className="fixed bottom-0 left-0 right-0 z-50 bg-[#0a0a0a] border-t border-white/5">
        <div className="max-w-5xl mx-auto px-6 h-10 flex items-center justify-between">
          <button onClick={() => setOpen(true)} className="flex items-center gap-2 text-muted-foreground hover:text-white transition-colors" data-testid="command-bar-toggle">
            <Terminal className="w-4 h-4 text-emerald-400" />
            <span className="text-xs font-mono">Command Bar</span>
            <span className="text-[10px] text-muted-foreground ml-1">Ctrl+K</span>
          </button>
          <div className="flex items-center gap-2 text-[10px] text-muted-foreground font-mono">
            {selectedBpId && <span className="px-1.5 py-0.5 bg-emerald-500/10 text-emerald-400">BP</span>}
            {selectedBizId && <span className="px-1.5 py-0.5 bg-violet-500/10 text-violet-400">BIZ</span>}
            <span>{page}</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed bottom-0 left-0 right-0 z-50 bg-[#0a0a0a] border-t border-white/5 shadow-2xl" data-testid="command-bar-expanded">
      <div className="max-w-5xl mx-auto px-6 h-9 flex items-center justify-between border-b border-white/5">
        <div className="flex items-center gap-2">
          <Terminal className="w-3.5 h-3.5 text-emerald-400" />
          <span className="text-xs font-mono font-bold text-muted-foreground">OPERATOR AGENT</span>
          {selectedBpId && <span className="text-[10px] font-mono px-1.5 py-0.5 bg-emerald-500/10 text-emerald-400">bp:{selectedBpId.slice(0, 8)}</span>}
          {selectedBizId && <span className="text-[10px] font-mono px-1.5 py-0.5 bg-violet-500/10 text-violet-400">biz</span>}
        </div>
        <button onClick={() => setOpen(false)} className="p-1 text-muted-foreground hover:text-white"><X className="w-3.5 h-3.5" /></button>
      </div>

      {result && (
        <div className="max-w-5xl mx-auto px-6 py-3 border-b border-white/5 max-h-48 overflow-y-auto">
          <p className="text-xs leading-relaxed whitespace-pre-wrap">{result.assistant_message}</p>
          {result.patch_summary && <p className="text-[10px] text-emerald-400 mt-1.5 font-mono">{result.patch_summary}</p>}
          {result.blueprint_name && <p className="text-[10px] text-emerald-400 mt-1 font-mono">Created: {result.blueprint_name}</p>}
          {result.available_blueprints && (
            <div className="mt-2 space-y-1">
              {result.available_blueprints.map(bp => (
                <button key={bp.id} onClick={() => { setSelectedBpId(bp.id); setResult(null); toast.success(`Selected: ${bp.name}`); }} className="block w-full text-left px-2 py-1.5 bg-white/5 hover:bg-white/10 text-xs transition-colors">
                  <span className="text-white">{bp.name}</span> <span className="text-muted-foreground font-mono">[{bp.status}]</span>
                </button>
              ))}
            </div>
          )}
          {result.validation_report && (
            <div className="flex items-center gap-1.5 mt-1.5">
              {result.validation_report.valid ? <><CheckCircle2 className="w-3 h-3 text-emerald-400" /><span className="text-[10px] text-emerald-400">Valid</span></> : <><AlertTriangle className="w-3 h-3 text-amber-400" /><span className="text-[10px] text-amber-400">{result.validation_report.warnings?.length || 0} warnings</span></>}
            </div>
          )}
          {result.next_actions?.length > 0 && (
            <div className="flex gap-1.5 mt-2">
              {result.next_actions.includes('apply_patch') && pendingPatch && (
                <Button onClick={handleApply} disabled={loading} size="sm" className="bg-emerald-500 text-black hover:bg-emerald-400 rounded-sm h-6 text-[10px] px-2"><CheckCircle2 className="w-3 h-3 mr-0.5" /> Apply</Button>
              )}
              {result.next_actions.includes('simulate') && (
                <Button onClick={() => { setMode('simulate'); setTimeout(handleSend, 100); }} disabled={loading} size="sm" variant="outline" className="border-white/10 rounded-sm h-6 text-[10px] px-2"><Play className="w-3 h-3 mr-0.5" /> Test</Button>
              )}
              {result.next_actions.includes('deploy') && (
                <Button onClick={() => { setMode('deploy'); setTimeout(handleSend, 100); }} disabled={loading} size="sm" variant="outline" className="border-white/10 rounded-sm h-6 text-[10px] px-2"><Rocket className="w-3 h-3 mr-0.5" /> Deploy</Button>
              )}
              {result.next_actions.includes('create') && (
                <Button onClick={() => setMode('create')} size="sm" variant="outline" className="border-emerald-500/20 text-emerald-400 rounded-sm h-6 text-[10px] px-2"><Plus className="w-3 h-3 mr-0.5" /> Create New</Button>
              )}
            </div>
          )}
        </div>
      )}

      <div className="max-w-5xl mx-auto px-6 py-2.5 flex items-center gap-2">
        <div className="flex bg-black/30 border border-white/10 rounded-sm overflow-hidden flex-shrink-0">
          {MODES.map(m => (
            <button key={m.value} onClick={() => setMode(m.value)} className={`px-2 py-1 text-[10px] font-mono transition-colors ${mode === m.value ? 'bg-emerald-500/20 text-emerald-400' : 'text-muted-foreground hover:text-white'}`} data-testid={`mode-${m.value}`}>{m.label}</button>
          ))}
        </div>
        <Input
          ref={inputRef}
          value={input}
          onChange={e => handleInputChange(e.target.value)}
          onKeyDown={e => e.key === 'Enter' && handleSend()}
          placeholder={
            mode === 'create' ? 'Describe the workflow to create...' :
            mode === 'chat' ? 'Ask anything...' :
            mode === 'simulate' ? 'Enter to simulate...' :
            mode === 'deploy' ? 'Enter to deploy...' :
            'Type a command...'
          }
          className="flex-1 bg-black/30 border-white/10 rounded-sm h-8 text-xs"
          disabled={loading}
          data-testid="command-input"
        />
        <Button onClick={handleSend} disabled={loading} className="bg-emerald-500 text-black hover:bg-emerald-400 rounded-sm h-8 px-3" data-testid="command-send">
          {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
        </Button>
      </div>
    </div>
  );
};

export default CommandBar;
